// Import styles
import '../css/styles.css';

const THEME_STORAGE_KEY = 'okif-theme';
const LANGUAGE_STORAGE_KEY = 'okif-language-v3';
const SUPPORTED_LANGUAGES = {
    en: 'EN',
    ar: 'AR',
    fr: 'FR',
    es: 'ES',
    pt: 'PT',
    bn: 'BN',
};

function getSavedTheme() {
    try {
        const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
        return savedTheme === 'light' || savedTheme === 'dark' ? savedTheme : null;
    } catch (error) {
        return null;
    }
}

function applyTheme(theme) {
    const isDark = theme === 'dark';
    const nextThemeLabel = isDark ? 'Switch to light mode' : 'Switch to dark mode';
    const root = document.documentElement;

    root.classList.add('theme-changing');
    root.classList.toggle('dark', isDark);
    root.dataset.theme = theme;
    root.style.colorScheme = theme;

    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) {
        themeColor.setAttribute('content', isDark ? '#0b1120' : '#ffffff');
    }

    document.querySelectorAll('.theme-toggle').forEach((button) => {
        button.setAttribute('aria-label', nextThemeLabel);
        button.setAttribute('title', nextThemeLabel);
        button.setAttribute('aria-pressed', String(isDark));

        const label = button.querySelector('.theme-toggle-label');
        if (label) {
            label.textContent = nextThemeLabel;
        }
    });

    // Keep theme changes synchronized while preserving normal hover transitions.
    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => root.classList.remove('theme-changing'));
    });
}

// Theme toggle and system preference sync
document.addEventListener('DOMContentLoaded', function () {
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)');
    const initialTheme = getSavedTheme() || (systemTheme.matches ? 'dark' : 'light');
    applyTheme(initialTheme);

    document.querySelectorAll('.theme-toggle').forEach((button) => {
        button.addEventListener('click', function () {
            const nextTheme = document.documentElement.classList.contains('dark') ? 'light' : 'dark';

            try {
                localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
            } catch (error) {
                // The selected theme still applies for this page if storage is unavailable.
            }

            applyTheme(nextTheme);
        });
    });

    const handleSystemThemeChange = (event) => {
        if (!getSavedTheme()) {
            applyTheme(event.matches ? 'dark' : 'light');
        }
    };

    if (typeof systemTheme.addEventListener === 'function') {
        systemTheme.addEventListener('change', handleSystemThemeChange);
    } else if (typeof systemTheme.addListener === 'function') {
        systemTheme.addListener(handleSystemThemeChange);
    }
});

// Site-wide language selector backed by Google Website Translate.
document.addEventListener('DOMContentLoaded', function () {
    const switcher = document.querySelector('.language-switcher');
    const toggle = document.getElementById('language-toggle');
    const menu = document.getElementById('language-menu');
    const currentLabel = document.querySelector('.current-language');

    if (!switcher || !toggle || !menu || !currentLabel) {
        return;
    }

    let selectedLanguage = 'en';
    try {
        const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (savedLanguage && SUPPORTED_LANGUAGES[savedLanguage]) {
            selectedLanguage = savedLanguage;
        }
    } catch (error) {
        // Continue with English if storage is unavailable.
    }

    const updateLanguageInterface = (language) => {
        const visibleLanguageLabel = SUPPORTED_LANGUAGES[language] || 'EN';
        currentLabel.textContent = visibleLanguageLabel;
        toggle.setAttribute('aria-label', `${visibleLanguageLabel}, select language`);
        document.documentElement.lang = language;
        document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';

        const heroTitle = document.querySelector('.hero-title');
        if (heroTitle) {
            heroTitle.setAttribute('aria-label', language === 'bn' ? heroTitle.dataset.labelBn : heroTitle.dataset.labelEn);
        }

        menu.querySelectorAll('[data-language]').forEach((button) => {
            const isActive = button.dataset.language === language;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
    };

    const closeLanguageMenu = () => {
        menu.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
    };

    let pendingLanguage = selectedLanguage;

    const applyTranslation = (language, attemptsRemaining = 30) => {
        const translateSelect = document.querySelector('.goog-te-combo');
        if (translateSelect) {
            if (translateSelect.value !== language) {
                translateSelect.value = language;
                translateSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
            return;
        }

        if (attemptsRemaining > 0) {
            window.setTimeout(() => applyTranslation(language, attemptsRemaining - 1), 200);
        }
    };

    const loadTranslationEngine = (language) => {
        pendingLanguage = language;

        const translationPolicy = document.getElementById('translation-policy');
        if (translationPolicy) {
            translationPolicy.remove();
        }

        let translateElement = document.getElementById('google_translate_element');
        if (!translateElement) {
            translateElement = document.createElement('div');
            translateElement.id = 'google_translate_element';
            translateElement.className = 'google-translate-element';
            translateElement.setAttribute('aria-hidden', 'true');
            document.body.appendChild(translateElement);
        }

        window.googleTranslateElementInit = function () {
            if (!translateElement.dataset.initialized && window.google && window.google.translate) {
                new window.google.translate.TranslateElement({
                    pageLanguage: 'en',
                    includedLanguages: 'ar,bn,en,es,fr,pt',
                    autoDisplay: false,
                }, 'google_translate_element');
                translateElement.dataset.initialized = 'true';
            }
            applyTranslation(pendingLanguage);
        };

        if (window.google && window.google.translate) {
            window.googleTranslateElementInit();
            return;
        }

        if (!document.getElementById('google-translate-script')) {
            const translateScript = document.createElement('script');
            translateScript.id = 'google-translate-script';
            translateScript.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
            translateScript.async = true;
            document.body.appendChild(translateScript);
        }
    };

    const selectLanguage = (language) => {
        if (!SUPPORTED_LANGUAGES[language]) {
            return;
        }

        selectedLanguage = language;
        try {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
        } catch (error) {
            // Translation still applies for the current page if storage is unavailable.
        }

        if (language === 'en') {
            document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
            document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
            document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
        } else {
            document.cookie = `googtrans=/en/${language}; path=/`;
        }

        updateLanguageInterface(language);
        closeLanguageMenu();

        if (language === 'en') {
            applyTranslation(language, 1);
        } else {
            loadTranslationEngine(language);
        }
    };

    updateLanguageInterface(selectedLanguage);

    toggle.addEventListener('click', () => {
        const willOpen = menu.hidden;
        menu.hidden = !willOpen;
        toggle.setAttribute('aria-expanded', String(willOpen));

        if (willOpen) {
            const activeOption = menu.querySelector('.is-active');
            if (activeOption) {
                activeOption.focus();
            }
        }
    });

    menu.querySelectorAll('[data-language]').forEach((button) => {
        button.addEventListener('click', () => selectLanguage(button.dataset.language));
    });

    document.addEventListener('click', (event) => {
        if (!switcher.contains(event.target)) {
            closeLanguageMenu();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !menu.hidden) {
            closeLanguageMenu();
            toggle.focus();
        }
    });

    if (selectedLanguage !== 'en') {
        loadTranslationEngine(selectedLanguage);
    }
});

// Smooth scroll for navigation links
document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (e) {
            const href = this.getAttribute('href');
            if (!href || href === '#') {
                e.preventDefault();
                return;
            }
            e.preventDefault();
            let target = null;
            try {
                target = document.querySelector(href);
            } catch (error) {
                return;
            }
            if (target) {
                // Safari may crash if smooth is not supported or if target is not in the DOM
                try {
                    if ('scrollBehavior' in document.documentElement.style) {
                        target.scrollIntoView({ behavior: 'smooth' });
                    } else {
                        target.scrollIntoView();
                    }
                } catch (err) {
                    // fallback: jump to element
                    target.scrollIntoView();
                }
            }
        });
    });
});

// Cursor follow glow effect
document.addEventListener('DOMContentLoaded', function () {
    const heroSection = document.getElementById('home-hero');
    if (!heroSection) {
        return;
    }

    const magicParticles = heroSection.querySelectorAll('.magic-particle');
    magicParticles.forEach((particle) => {
        const duration = 6 + Math.random() * 6;
        const delay = -(Math.random() * duration);
        const left = Math.random() * 100;
        const size = 2 + Math.random() * 3;
        const drift = (Math.random() * 44 - 22).toFixed(1);
        const peakOpacity = (0.45 + Math.random() * 0.45).toFixed(2);

        particle.style.setProperty('--magic-duration', `${duration.toFixed(2)}s`);
        particle.style.setProperty('--magic-delay', `${delay.toFixed(2)}s`);
        particle.style.setProperty('--magic-left', `${left.toFixed(2)}%`);
        particle.style.setProperty('--magic-size', `${size.toFixed(2)}px`);
        particle.style.setProperty('--magic-drift', `${drift}px`);
        particle.style.setProperty('--magic-opacity', peakOpacity);
    });

    const cursorGlow = document.createElement('div');
    cursorGlow.classList.add('cursor-glow');
    cursorGlow.style.opacity = '0';
    document.body.appendChild(cursorGlow);

    let mouseX = 0;
    let mouseY = 0;
    let currentX = 0;
    let currentY = 0;
    let isInHero = false;

    heroSection.addEventListener('mouseenter', () => {
        isInHero = true;
        cursorGlow.style.opacity = '1';
    });

    heroSection.addEventListener('mouseleave', () => {
        isInHero = false;
        cursorGlow.style.opacity = '0';
    });

    heroSection.addEventListener('mousemove', (e) => {
        mouseX = e.clientX;
        mouseY = e.clientY;
    });

    function animate() {
        if (isInHero) {
            currentX += (mouseX - currentX) * 0.1;
            currentY += (mouseY - currentY) * 0.1;
            cursorGlow.style.left = currentX + 'px';
            cursorGlow.style.top = currentY + 'px';
        }

        requestAnimationFrame(animate);
    }

    animate();
});

// Form submission handler
document.addEventListener('DOMContentLoaded', function () {
    const contactForm = document.querySelector('.contact-form form');
    if (contactForm) {
        contactForm.addEventListener('submit', function (e) {
            e.preventDefault();
            alert('Thank you for your message! We will get back to you soon.');
            this.reset();
        });
    }
});

console.log('OKIF Website loaded');

// Lighthouse light beam following mouse cursor
document.addEventListener('DOMContentLoaded', function () {
    const lightBeam = document.getElementById('light-beam');
    const lighthouseContainer = document.getElementById('lighthouse-container');
    
    if (lightBeam && lighthouseContainer) {
        document.addEventListener('mousemove', function(e) {
            const containerRect = lighthouseContainer.getBoundingClientRect();
            const lighthouseX = containerRect.left + containerRect.width / 2;
            const lighthouseY = containerRect.top + containerRect.height * 0.27; // Adjusted for new lighthouse position
            
            const mouseX = e.clientX;
            const mouseY = e.clientY;
            
            // Calculate angle between lighthouse and mouse
            const deltaX = mouseX - lighthouseX;
            const deltaY = mouseY - lighthouseY;
            const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
            
            // Rotate the light beam to point at mouse cursor
            lightBeam.style.transform = `rotate(${angle}deg)`;
        });
    }
});

// Enable Hot Module Replacement
if (typeof module !== 'undefined' && module.hot) {
    module.hot.accept('../css/styles.css', function () {
        console.log('CSS updated!');
    });

    module.hot.accept();
}
